import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { CheckCircle, Clock } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { COINIS_LOGO_DARK } from '../../../config';
import { usersService } from '../../../services/users';
import { generateErrorMessages } from '../../../utils/showApiErrors';
import { emailSchema } from '../schemas/email';

export const LinkError = ({
  title = '',
  description = '',
  circleClassName = '',
  icon
}: {
  title: string;
  description: string;
  circleClassName: string;
  icon: ReactNode;
}): JSX.Element => {
  type FormValues = z.infer<typeof emailSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' }
  });
  const {
    handleSubmit,
    control,
    formState: { errors: emailError, isValid, isDirty }
  } = form;

  const emailInput = useWatch({ control, name: 'email' });
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | string[]>('');

  const onSubmit = async (data: { email: string }): Promise<void> => {
    try {
      if (!isValid || !isDirty) {
        return;
      }

      const email = data.email;
      await usersService.resendSetupMailWithUserMail(email);
      setSuccess(true);
    } catch (e) {
      const axiosError = e as AxiosError;
      setError(generateErrorMessages(axiosError));
      setSuccess(false);
    }
  };

  if (success) {
    return (
      <>
        <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />

        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-onboarding-success-light">
            <CheckCircle className="h-5 w-5 text-onboarding-success" />
          </div>
          <div className="font-onboarding-1 text-[1.625rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary">
            Check your email
          </div>
        </div>

        <p className="text-[15px] font-medium leading-[1.5] text-[var(--ink-2)]">
          If <span className="font-semibold text-[var(--ink)]">{emailInput}</span> is registered, you&apos;ll receive an
          account setup link shortly.
        </p>

        <div className="rounded-xl border border-[var(--line)] bg-[var(--sunken)] px-4 py-4">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-[var(--ink-3)]">Next steps</p>
          <ol className="flex flex-col gap-2 text-sm font-medium text-[var(--ink)]">
            {[
              'Check your email inbox',
              'Click the secure setup link',
              'Set up your account',
              'Sign in with 2FA verification'
            ].map((step, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ff6a40] text-[10px] font-bold text-white">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-brand-gold/30 bg-brand-gold-50 px-4 py-3">
          <Clock className="h-4 w-4 shrink-0 text-brand-gold" />
          <p className="text-sm font-semibold text-brand-orange">Link expires in 24 hours</p>
        </div>

        <p className="text-center text-sm text-[var(--ink-2)]">
          Didn&apos;t receive it?{' '}
          <button
            type="button"
            onClick={() => setSuccess(false)}
            className="cursor-pointer font-semibold text-brand-blue"
          >
            Resend email
          </button>
        </p>
      </>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate className="contents">
        <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />

        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${circleClassName}`}>
            {icon}
          </div>
          <div className="font-onboarding-1 text-[1.625rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary">
            {title}
          </div>
        </div>

        <p className="text-[15px] font-medium leading-[1.5] text-[var(--ink-2)]">{description}</p>

        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[13px] font-semibold text-[var(--ink)]">Your email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  enterKeyHint="send"
                  placeholder="example@email.com"
                  aria-invalid={form.formState.errors.email ? 'true' : 'false'}
                  {...field}
                />
              </FormControl>
              <FormMessage>{emailError.email?.message}</FormMessage>
            </FormItem>
          )}
        />

        {error && (
          <div className="text-loss text-sm font-medium">
            {Array.isArray(error) ? error.map((err, i) => <div key={i}>{err}</div>) : error}
          </div>
        )}

        <button
          type="submit"
          className="btn-cta-orange flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
        >
          Request new link
        </button>
      </form>
    </Form>
  );
};
